/* Messages — conversation list beside the conversation. */

/*
  *** TWO COLUMNS, NOT TWO SCREENS ***
  The app has these as separate bottom-nav screens because a phone has no
  room for both. A portal does, and switching conversations without losing
  the list is the whole advantage of the wider screen.

  A fixed height, not `min-height`: the message area scrolls INSIDE the
  panel, so the composer stays put and the page itself does not grow.
*/
.layout {
  display: grid;
  grid-template-columns: 18rem minmax(0, 1fr);
  gap: var(--space-4);
  /* The shell's header and padding. Leaves the page itself unscrolled. */
  height: calc(100dvh - 14rem);
  min-height: 24rem;
}

/* ------------------------------------------------------- the thread list */

.threads {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  overflow-y: auto;
  padding: var(--space-2);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
}

.thread {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3);
  font: inherit;
  text-align: left;
  color: var(--text);
  background: none;
  border: 0;
  border-radius: var(--radius);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.thread:hover {
  background: var(--bg-hover);
}

.threadOn {
  background: var(--indigo-soft);
}

.threadAvatar {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 2.25rem;
  height: 2.25rem;
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--indigo);
  background: var(--indigo-soft);
  border-radius: var(--radius-full);
}

.threadOn .threadAvatar {
  color: #fff;
  background: var(--indigo);
}

/* `min-width: 0` so a long name truncates instead of widening the column. */
.threadText {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.threadTop {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-2);
}

.threadName {
  font-size: var(--text-base);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.threadWhen {
  flex-shrink: 0;
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.threadSub {
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.unread {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.3rem;
  font-size: var(--text-xs);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #fff;
  background: var(--indigo);
  border-radius: var(--radius-full);
}

/* ------------------------------------------------------ the conversation */

.panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.panelHead {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--border);
}

.panelName {
  font-size: var(--text-md);
  font-weight: 600;
}

/* Only meaningful when the columns are stacked. */
.back {
  display: none;
}

/*
  *** THIS IS THE ONLY THING THAT SCROLLS ***
  Keeping the composer out of the scroll area is why the whole layout has a
  fixed height.
*/
.messages {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  flex: 1;
  overflow-y: auto;
  padding: var(--space-4);
}

.olderNote {
  margin: 0 auto var(--space-2);
  font-size: var(--text-xs);
  color: var(--text-subtle);
}

/* "Load older messages", above the first bubble. */
.olderRow {
  display: flex;
  justify-content: center;
  /* Stops the button sticking to the oldest bubble when a page lands. */
  padding-bottom: var(--space-2);
}

/* ------------------------------------------------------------- bubbles */

.bubble {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  /* Capped so a long message does not run the full width of a wide panel,
     which is hard to read and loses the sense of a conversation. */
  max-width: min(36rem, 78%);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-lg);
}

/* Mine right and filled, theirs left and plain — the universal convention,
   and the fastest way to tell them apart without reading. */
.mine {
  align-self: flex-end;
  color: #fff;
  background: var(--indigo);
  border-bottom-right-radius: var(--radius-sm);
}

.theirs {
  align-self: flex-start;
  color: var(--text);
  background: var(--bg-sunken);
  border-bottom-left-radius: var(--radius-sm);
}

.body {
  margin: 0;
  font-size: var(--text-base);
  line-height: var(--leading-normal);
  /* Preserve the sender's own line breaks; wrap a long unbroken string
     rather than widening the bubble. */
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.meta {
  align-self: flex-end;
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
  opacity: 0.7;
}

.readMark {
  font-weight: 600;
}

/* --------------------------------------------------------- attachments */

.attachment {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.image {
  display: block;
  max-width: 100%;
  max-height: 18rem;
  border-radius: var(--radius);
}

.attachNote {
  font-size: var(--text-sm);
  opacity: 0.85;
}

.file {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3);
  background: rgb(255 255 255 / 14%);
  border-radius: var(--radius);
}

/* On a light bubble that white wash is invisible. */
.theirs .file {
  background: var(--bg-elevated);
  border: 1px solid var(--border);
}

.fileIcon svg {
  width: 1.5rem;
  height: 1.5rem;
}

.fileText {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.fileName {
  font-size: var(--text-sm);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fileSize {
  font-size: var(--text-xs);
  opacity: 0.75;
}

.fileOpen {
  flex-shrink: 0;
  padding: var(--space-1) var(--space-3);
  font: inherit;
  font-size: var(--text-sm);
  font-weight: 500;
  color: inherit;
  text-decoration: underline;
  background: none;
  border: 0;
  cursor: pointer;
}

/* --------------------------------------------------------- the composer */

.composer {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-top: 1px solid var(--border);
}

.composerRow {
  display: flex;
  align-items: flex-end;
  gap: var(--space-2);
}

.input {
  flex: 1;
  min-width: 0;
  /* Grows with the message, up to a point, then scrolls. */
  min-height: 2.5rem;
  max-height: 8rem;
  padding: var(--space-2) var(--space-3);
  font: inherit;
  font-size: var(--text-base);
  color: var(--text);
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  resize: vertical;
}

.input:focus {
  outline: 2px solid var(--indigo);
  outline-offset: -1px;
}

.hiddenFile {
  display: none;
}

.pending {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  align-self: flex-start;
  padding: var(--space-1) var(--space-3);
  font-size: var(--text-sm);
  background: var(--indigo-soft);
  border-radius: var(--radius-full);
}

.pendingName {
  max-width: 14rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pendingX {
  font: inherit;
  font-size: var(--text-md);
  line-height: 1;
  color: var(--text-muted);
  background: none;
  border: 0;
  cursor: pointer;
}

.quotaNote {
  margin: 0;
  font-size: var(--text-xs);
  color: var(--text-muted);
}

/* The lock, in place of the composer. */
.locked {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--warning-soft);
  border-top: 1px solid var(--border);
}

.lockedText {
  margin: 0;
  flex: 1 1 16rem;
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text);
}

/* ---------------------------------------------------------------- misc */

.muted {
  margin: auto;
  padding: var(--space-5);
  color: var(--text-muted);
  font-size: var(--text-sm);
  text-align: center;
}

.error {
  margin: 0;
  color: var(--danger);
  font-size: var(--text-sm);
}

/* -------------------------------------------------------------- narrow */

@media (max-width: 52rem) {
  /*
    *** ONE AT A TIME, LIKE THE APP ***
    Two columns on a phone gives neither enough width. The list is the
    screen until a conversation is opened, and Back returns to it.
  */
  .layout {
    grid-template-columns: minmax(0, 1fr);
    height: calc(100dvh - 12rem);
  }

  .layout .panel {
    display: none;
  }

  .layoutOpen .threads {
    display: none;
  }

  .layoutOpen .panel {
    display: flex;
  }

  .back {
    display: inline-flex;
  }

  .bubble {
    max-width: 88%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .thread {
    transition: none;
  }
}
